// Eskom PARCC — Lumen sidebar (256px rail, collapsible to 76px).
//
// Grouped navigation derived from the SAME `APP_ROUTES` table and the SAME
// `canAccessRoute(path, grant)` predicate the Legacy nav and the router guard
// use — there is no second gating rule to drift. Grouping and the collapsed rail
// are presentation: the set of reachable screens is identical in both themes.
import { NavLink, useLocation } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../../lib/auth';
import { useTheme } from '../../../lib/theme';
import { canAccessRoute } from '../../../lib/permissions';
import { useTransactions } from '../../../hooks';
import { APP_ROUTES, NAV_GROUPS, type AppRoute } from '../routes';
import { ViewAsControl } from '../ViewAsControl';

/** Initials for the identity chip, from a display name or an email local part. */
function initialsOf(name: string): string {
  const parts = name
    .replace(/[._-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function LumenSidebar({
  collapsed,
  onOpenAccount,
}: {
  collapsed: boolean;
  onOpenAccount: () => void;
}): React.JSX.Element {
  const { grant, realRole, user } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  // The register is already in the query cache for every signed-in user, so the
  // count pill is free — no extra fetch.
  const { data: transactions } = useTransactions();

  // Collapsed-rail hover tooltip: the label of the hovered icon + its vertical
  // centre. Rendered as a portalled fixed pill (see below).
  const [tip, setTip] = useState<{ label: string; top: number } | null>(null);

  const visible = useMemo(
    () =>
      APP_ROUTES.filter(
        (route) => canAccessRoute(route.path, grant) && route.lumenParent === undefined,
      ),
    [grant],
  );

  const txCount = (transactions ?? []).filter((t) => !t.deleted).length;
  const activeSegment = location.pathname.replace(/^\/+/, '').split('/')[0] ?? '';

  /** A child route folded into this item (e.g. Bulk Update under Import & Bulk)
   *  keeps the parent highlighted, so a deep link never orphans the nav. */
  function isActive(route: AppRoute): boolean {
    if (activeSegment === route.path) return true;
    return APP_ROUTES.some((r) => r.lumenParent === route.path && r.path === activeSegment);
  }

  const displayName = user?.displayName?.trim() || user?.email?.split('@')[0] || 'Signed in';
  const initials = initialsOf(displayName);

  return (
    <aside className={`lumen-sidebar${collapsed ? ' is-collapsed' : ''}`}>
      <div className="lumen-brand">
        {collapsed ? (
          <span className="mark">
            <img src="/logos/eskom-mark.svg" alt="Eskom" />
          </span>
        ) : (
          <img
            className="wordmark"
            src={theme === 'dark' ? '/logos/eskom-parcc-wht.svg' : '/logos/eskom-parcc-blk.svg'}
            alt="Eskom PARCC — Procurement Audit Recovery Command Centre"
          />
        )}
      </div>

      <nav aria-label="Primary">
        {NAV_GROUPS.map((group) => {
          const items = visible.filter((r) => r.group === group.key);
          if (items.length === 0) return null;
          return (
            <div key={group.key}>
              {!collapsed && <div className="lumen-navgroup">{group.label}</div>}
              {items.map((route) => {
                const Icon = route.icon;
                const label = route.lumenLabel ?? route.label;
                return (
                  <NavLink
                    key={route.path}
                    to={`/${route.path}`}
                    className={`lumen-navlink${isActive(route) ? ' active' : ''}`}
                    // Collapsed: a styled pill tooltip (below) replaces the native
                    // title — the rail is a scroll container, so the pill is
                    // portalled fixed to escape its clipping.
                    aria-label={collapsed ? label : undefined}
                    onMouseEnter={
                      collapsed
                        ? (e) => {
                            const r = e.currentTarget.getBoundingClientRect();
                            setTip({ label, top: Math.round(r.top + r.height / 2) });
                          }
                        : undefined
                    }
                    onMouseLeave={collapsed ? () => setTip(null) : undefined}
                  >
                    <Icon size={18} aria-hidden style={{ flex: 'none', position: 'relative' }} />
                    {!collapsed && <span style={{ position: 'relative' }}>{label}</span>}
                    {!collapsed && route.path === 'transactions' && txCount > 0 && (
                      <span className="lumen-navcount">{txCount.toLocaleString('en-ZA')}</span>
                    )}
                    {/* Decoration only. `aria-hidden` matters: without it the
                        link's accessible name becomes "AI Workspace NEW", which
                        reads badly to a screen reader and would quietly break
                        any name-based nav assertion. */}
                    {!collapsed && route.badge !== undefined && (
                      <span className="lumen-navbadge" aria-hidden>
                        {route.badge}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="lumen-sidefoot">
        <button type="button" className="lumen-identity" onClick={onOpenAccount} title="My account">
          {user?.photoURL ? (
            <img className="lumen-avatar" src={user.photoURL} alt="" />
          ) : (
            <span className="lumen-avatar" aria-hidden>
              {initials}
            </span>
          )}
          {!collapsed && (
            <span className="who">
              <b>{displayName}</b>
              <span>{user?.email ?? ''}</span>
            </span>
          )}
        </button>
        {/* Admin-only preview control. Mounted only for admins so the
            access-grants query never fires for anybody else — same rule as the
            Legacy header. */}
        {!collapsed && realRole === 'admin' && (
          <div className="lumen-viewas">
            <span>Viewing as</span>
            <ViewAsControl />
          </div>
        )}
      </div>

      {/* Collapsed-rail label pill. Portalled to <body> so it escapes the rail's
          overflow clipping, and positioned to the right of the 76px rail at the
          hovered icon's vertical centre. */}
      {collapsed &&
        tip &&
        createPortal(
          <div className="lumen-rail-tip" style={{ top: tip.top }} role="tooltip">
            {tip.label}
          </div>,
          document.body,
        )}
    </aside>
  );
}
